import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useGetUserDataQuery, useUpdatePasswordMutation, useUpdateUserDataMutation } from '../services/auth';
import { useNavigate, useParams } from 'react-router-dom';
import { BoxArrowRight, Gear, Gem, Grid1x2, PersonCircle } from 'react-bootstrap-icons';
import { Button, Dropdown, Modal, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setAuthToken } from '../services/GlobalSlice';
import { toast } from 'react-toastify';
import { toggleTheme } from '../services/themeSlice'; 
import { Classic } from '@theme-toggles/react'; 
import '@theme-toggles/react/css/Classic.css'; 
import './ChatHeader.css'
import { userApi, useGetRoomsQuery } from '../services/user';

const UserDropdown = ({ handleLogout, openSettings, openUpgradePlan }) => {
  const navigate = useNavigate();
  const { data: userData, isLoading, error } = useGetUserDataQuery();
  const { theme } = useSelector((state) => state.theme); // Get theme from Redux

  useEffect(() => {
    if (error) {
      navigate('/login', { replace: true });
    }
  }, [error, navigate]);

  if (isLoading) {
    return <PersonCircle size={20} color="gray" />;
  }

  return (
    <Dropdown>
      <Dropdown.Toggle
        as="div"
        bsPrefix="custom-toggle"
        id="user-dropdown"
        className="d-flex align-items-center"
        style={{ cursor: 'pointer' }}
      >
        <PersonCircle size={20} color={theme === 'dark' ? 'rgba(255,255,255,0.5)' : '#6b7280'} />
      </Dropdown.Toggle>
      <Dropdown.Menu align="end" className="dropdown-menu">
        {userData?.is_superuser && (
          <Dropdown.Item
            className="admin-option"
            onClick={() => navigate('/admin-dashboard')}
          >
            <Grid1x2 className="me-2" /> Admin Dashboard
          </Dropdown.Item>
        )}
        {(userData?.role || '').toLowerCase() === 'tenant_admin' && (
          <Dropdown.Item
            className="admin-option"
            onClick={() => navigate('/tenant-admin')}
          >
            <Grid1x2 className="me-2" /> Workspace Admin
          </Dropdown.Item>
        )}
        <Dropdown.Item onClick={openSettings}>
          <Gear className="me-2" /> Settings
        </Dropdown.Item>
        <Dropdown.Item onClick={openUpgradePlan}>
          <Gem className="me-2" /> Upgrade Plan
        </Dropdown.Item>
        <Dropdown.Item onClick={handleLogout}>
          <BoxArrowRight className="me-2" /> Logout
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};

const ChatHeader = ({ referenceDoc = null, onAttachReferenceDoc, onClearReferenceDoc }) => {
  const { roomId } = useParams();
  const refDocInputRef = useRef(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showUpgradePlan, setShowUpgradePlan] = useState(false);
  const [showUpdatePassword, setShowUpdatePassword] = useState(false);
  const { theme } = useSelector((state) => state.theme); // Get theme from Redux
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authToken = useSelector((state) => state.global.authToken) || localStorage.getItem("access_token");
  const { data: roomsData } = useGetRoomsQuery(undefined, { skip: !authToken });

  const currentChatTitle = useMemo(() => {
    if (!roomId) return 'New chat';
    if (!roomsData) return 'Chat';
    const allBuckets = Object.values(roomsData || {}).flatMap((arr) => (Array.isArray(arr) ? arr : []));
    const match = allBuckets.find((r) => r?.uuid_field === roomId);
    return match?.chat_room_name || 'Chat';
  }, [roomId, roomsData]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme || 'dark');
  }, [theme]);

  const { data: userData, refetch } = useGetUserDataQuery();
  const [updateUserData] = useUpdateUserDataMutation();
  const [updatePassword] = useUpdatePasswordMutation();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    email: '',
  });

  const handleUpdate = async () => {
    try {
      await updateUserData(formData).unwrap();
      toast.success('Profile updated successfully!');
      refetch();
    } catch {
      toast.error('Failed to update profile.');
    }
  };
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
  });

  useEffect(() => {
    if (userData) {
      setFormData({
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        company_name: userData.company_name || '',
        email: userData.email || '',
      });
    }
  }, [userData]);

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    try {
      await updatePassword({
        old_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
      }).unwrap();
      toast.success('Password updated successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '' });
      setShowUpdatePassword(false);
    } catch (error) {
      toast.error(error?.data?.error || 'Failed to update password.');
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('hasLogin');
    localStorage.removeItem('user');
    dispatch(setAuthToken(null));
    dispatch(userApi.util.resetApiState());
    navigate('/login');
  };

  const handleRefDocSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && onAttachReferenceDoc) {
      onAttachReferenceDoc(file);
    }
    e.target.value = '';
  };

  return (
    <div className="chat-header d-flex justify-content-between align-items-center">
      <div className="chat-header-left d-flex align-items-center gap-2">
        <div className="chat-title chat-header-title" title={currentChatTitle}>
          {currentChatTitle}
        </div>
        {roomId && (
          <>
            <input
              type="file"
              ref={refDocInputRef}
              style={{ display: 'none' }}
              accept=".pdf,.xlsx,.xls,.csv,.docx"
              onChange={handleRefDocSelect}
            />
            {!referenceDoc ? (
              <button
                type="button"
                className="reference-doc-btn"
                onClick={() => refDocInputRef.current?.click()}
                title="Attach reference document to this conversation"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                </svg>
                Reference doc
              </button>
            ) : (
              <div className="reference-doc-active">
                <span>📎 {referenceDoc}</span>
                <button type="button" onClick={onClearReferenceDoc} aria-label="Remove reference document">
                  ✕
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <div className="chat-header-actions d-flex align-items-center">
        <Classic
          toggled={theme === 'dark'}
          toggle={() => dispatch(toggleTheme())}
          className="theme-toggle"
          aria-label="Toggle theme"
        />
        <UserDropdown
          handleLogout={handleLogout}
          openSettings={() => setShowSettings(true)}
          openUpgradePlan={() => setShowUpgradePlan(true)}
        />
      </div>

      {/* Settings Modal */}
      <Modal show={showSettings} onHide={() => setShowSettings(false)} centered>
        <Modal.Header closeButton className="border-0 pb-2">
          <Modal.Title className="w-100 text-center fw-bold">Settings</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column align-items-center p-4">
          <Form className="w-100" style={{ maxWidth: '400px' }}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">First Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Last Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Company Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Email</Form.Label>
              <Form.Control type="text" value={formData.email} disabled />
            </Form.Group>
            <div className="d-flex flex-column gap-3 align-items-center mt-4">
              <Button variant="dark" className="w-100 py-2 fw-semibold" onClick={handleUpdate}>
                Save Changes
              </Button>
              <Button
                variant="link"
                className="text fw-semibold"
                onClick={() => {
                  setShowSettings(false);
                  setShowUpdatePassword(true);
                }}
              >
                Reset Password
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      <Modal show={showUpgradePlan} onHide={() => setShowUpgradePlan(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="w-100 text-center">Upgrade Plan</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column align-items-center">
          <p>For upgrades, contact: <a href="mailto:support@stratusedge.ai"><strong>support@stratusedge.ai</strong></a></p>
        </Modal.Body>
      </Modal>

      {/* Update Password Modal */}
      <Modal show={showUpdatePassword} onHide={() => setShowUpdatePassword(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="w-100 text-center">Update Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleUpdatePassword}>
            <div className="mb-3">
              <label htmlFor="currentPassword" className="form-label">
                Current Password
              </label>
              <input
                type="password"
                className="form-control"
                id="currentPassword"
                name="currentPassword"
                value={passwordForm.currentPassword}
                onChange={handlePasswordChange}
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="newPassword" className="form-label">
                New Password
              </label>
              <input
                type="password"
                className="form-control"
                id="newPassword"
                name="newPassword"
                value={passwordForm.newPassword}
                onChange={handlePasswordChange}
                required
              />
            </div>
            <div className="d-flex justify-content-center">
              <Button type="submit" variant="dark" className="w-100 py-2 fw-semibold">
                Update Password
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ChatHeader;
